import _ from 'lodash'
import modelApi from '../services/ModelApi'

export default {
  data: function () {
    return {
      isOpenTechnical: false,
      isOpenFinancial: false,
      title: '',
      tableLoading: true,
      technicalInputs: {},
      financialInputs: {},
      // Switches
      financialAssumptionsSwitch: 'Base Case',
      technicalAssumptionsSwitch: 'Base Case',
      viewSwitch: 'Chart',
      chartSwitch: 'Production',
      currencySwitch: 'USD',
      accountingSwitch: 'By-Product',
      // API Outputs
      baseCase: {},
      userCase: { 'valuation': { 'irr': 0.01, 'npv': 0 }, 'annual': { 'goldProduction': 0 }, 'years': [] },
      spotCase: {},
      // Store assumptions for resetting
      baseCaseFinancialAssumptions: {},
      baseCaseTechnicalAssumptions: {},
      spotPrices: {},
      projectStage: 'Development',
      // Flag to build table after load
      loaded: false,
      loading: true,
      tableData: [],
      // Information to create table
      aiscChartField: 'AISC',
      fieldSettings: {
        preTaxIrr: {
          apiField: 'preTaxIrr',
          unit: '%', // TODO: This should come from API
          label: 'Pre-Tax IRR',
          formatter: this.percent,
          currency: false,
          apiLocation: 'valuation'
        },
        preTaxNpv: {
          apiField: 'preTaxNpv',
          unit: 'M', // TODO: This should come from API
          label: 'Pre-Tax NPV',
          formatter: this.millions,
          currency: true,
          apiLocation: 'valuation'
        },
        irr: {
          apiField: 'irr',
          unit: '%', // TODO: This should come from API
          label: 'After-Tax IRR',
          formatter: this.percent,
          currency: false,
          apiLocation: 'valuation'
        },
        npv: {
          apiField: 'npv',
          unit: 'M', // TODO: This should come from API
          label: 'After-Tax NPV',
          formatter: this.millions,
          currency: true,
          apiLocation: 'valuation'
        },
        freeCashFlow: {
          apiField: 'freeCashFlow',
          unit: 'M', // TODO: This should come from API
          label: 'Free Cash Flow',
          formatter: this.millions,
          currency: true,
          apiLocation: 'valuation'
        },
        AISC: {
          apiField: 'AISC',
          unit: '/oz', // TODO: This should come from API
          label: 'AISC',
          formatter: this.number,
          currency: true,
          apiLocation: 'lom'
        },
        goldProduction: {
          apiField: 'goldProduction',
          unit: 'k oz', // TODO: This should come from API
          label: 'Gold Production',
          formatter: this.number,
          currency: false,
          apiLocation: 'lom'
        }
      },
      currencyLabels: {
        CAD: 'C$',
        USD: 'US$'
      },
      exchangeRate: 1,
      activeTechnicalCollapse: []
    }
  },
  created () {
    this.debouncedUpdateData = _.debounce(this.updateData, 300)
    let updateBase = this.updateBase()
    let updateSpot = this.fetchSpot().then(() => this.updateSpot())

    Promise.all([
      updateBase,
      updateSpot
    ]).then(() => {
      this.loaded = true
      this.loading = false
    })
  },
  computed: {
    inputs () {
      let technicalDifferences = this.differences(this.technicalInputs, this.baseCaseTechnicalAssumptions)
      let financialDifferences = this.differences(this.financialInputs, this.baseCaseFinancialAssumptions)
      return Object.assign({}, technicalDifferences, financialDifferences)
    }
  },
  methods: {
    fetchSpot () {
      return modelApi.fetchSpotPrices({}).then(response => {
        this.spotPrices = response.data
      })
    },
    copy (object) {
      return JSON.parse(JSON.stringify(object))
    },
    millions (field, exchange) {
      let multiplier = 1
      if (exchange) {
        multiplier = this.exchangeRate
      }
      return Math.round((field * multiplier) / 1000).toLocaleString()
    },
    number (field, exchange) {
      let multiplier = 1
      if (exchange) {
        multiplier = this.exchangeRate
      }
      return Math.round(field * multiplier).toLocaleString()
    },
    percent (field, exchange) {
      let multiplier = 1
      if (exchange) {
        multiplier = this.exchangeRate
      }
      return Math.round(field * 100 * multiplier).toLocaleString() + '%'
    },
    round (number, decimals) {
      return (
        Math.round(number * Math.pow(10, decimals)) / Math.pow(10, decimals)
      )
    },
    tableRow (field) {
      let settings = this.fieldSettings[field]
      let units = '('
      if (settings.currency) {
        units = units + this.currencyLabels[this.currencySwitch] + ' '
      }
      units = units + settings.unit + ')'
      return {
        field: settings.label,
        units: units,
        baseCase: settings.formatter(
          this.baseCase[settings.apiLocation][settings.apiField],
          settings.currency
        ),
        spotCase: settings.formatter(
          this.spotCase[settings.apiLocation][settings.apiField],
          settings.currency
        ),
        userCase: settings.formatter(
          this.userCase[settings.apiLocation][settings.apiField],
          settings.currency
        )
      }
    },
    updateBase () {
      return this.updateData({}, true)
    },
    updateSpot () {
      let params = Object.assign({}, this.technicalInputs, this.spotPrices)
      return modelApi.fetchModel(params, this.$route.params.id).then(response => {
        this.spotCase = response.data
      })
    },
    updateData: function (params, initialize) {
      return modelApi
        .fetchModel(params, this.$route.params.id)
        .then(response => {
          this.userCase = response.data
          if (initialize) {
            this.baseCase = response.data
            this.technicalInputs = {
              heapLeachGoldRecovery: response.data.lom.heapLeachGoldRecovery,
              heapLeachSilverRecovery:
              response.data.lom.heapLeachSilverRecovery,
              millGoldRecovery: response.data.lom.millGoldRecovery,
              millSilverRecovery: response.data.lom.millSilverRecovery,
              millCopperRecovery: response.data.lom.millCopperRecovery,
              millMolybdenumRecovery: response.data.lom.millMolybdenumRecovery,
              millRheniumRecovery: response.data.lom.millRheniumRecovery,
              stripRatio: response.data.lom.stripRatio,
              openPitMiningCostPerTonneMined:
              response.data.lom.openPitMiningCostPerTonneMined,
              undergroundMiningCostPerTonneMined:
              response.data.lom.undergroundMiningCostPerTonneMined,
              heapLeachProcessCostPerTonneProcessed:
              response.data.lom.heapLeachProcessCostPerTonneProcessed,
              millProcessCostPerTonneProcessed:
              response.data.lom.millProcessCostPerTonneProcessed,
              gAndACostPerTonneProcessed:
              response.data.lom.gAndACostPerTonneProcessed,
              heapLeachGoldGrade: response.data.lom.heapLeachGoldGrade,
              millGoldGrade: response.data.lom.millGoldGrade,
              heapLeachSilverGrade: response.data.lom.heapLeachSilverGrade,
              millSilverGrade: response.data.lom.millSilverGrade,
              millCopperGrade: response.data.lom.millCopperGrade,
              millMolybdenumGrade: response.data.lom.millMolybdenumGrade,
              millRheniumGrade: response.data.lom.millRheniumGrade,
              sustainingCapexVariance: 0,
              preProductionCapexVariance: 0
            }
            this.financialInputs = {
              discountRate: response.data.valuation.discountRate,
              delayStartYear: response.data.valuation.delayStartYear,
              goldPrice: response.data.macro.goldPrice,
              silverPrice: response.data.macro.silverPrice,
              copperPrice: response.data.macro.copperPrice,
              molybdenumPrice: response.data.macro.molybdenumPrice,
              rheniumPrice: response.data.macro.rheniumPrice,
              USDCAD: this.dataOrSpot(response.data.macro.USDCAD) // TODO: make this more general
            }
            this.baseCaseFinancialAssumptions = this.copy(this.financialInputs)
            this.baseCaseTechnicalAssumptions = this.copy(this.technicalInputs)
            this.title = response.data.model.assetName
          } else {
            this.buildTable()
          }
        })
        .catch(e => {
          console.log(e)
        })
    },
    buildTable () {
      this.tableData = [
        this.tableRow('preTaxIrr'),
        this.tableRow('preTaxNpv'),
        this.tableRow('irr'),
        this.tableRow('npv'),
        this.tableRow('freeCashFlow'),
        this.tableRow('AISC'),
        this.tableRow('goldProduction')
      ]
      this.tableLoading = false
    },
    dataOrSpot (data) {
      if (data === 0) {
        return this.spotPrices.USDCAD
      } else {
        return data
      }
    },
    differences (object, base) {
      function changes (object, base) {
        return _.transform(object, function (result, value, key) {
          if (!_.isEqual(value, base[key])) {
            result[key] = (_.isObject(value) && _.isObject(base[key])) ? changes(value, base[key]) : value
          }
        })
      }
      return changes(object, base)
    }

  },
  watch: {
    technicalInputs: {
      deep: true,
      handler () {
        if (this.loaded) {
          this.loading = true
          this.updateData(this.inputs, false).then(
            () => { this.loading = false }
          )
        }
        if (
          JSON.stringify(this.technicalInputs) ===
          JSON.stringify(this.baseCaseTechnicalAssumptions)
        ) {
          this.technicalAssumptionsSwitch = 'Base Case'
        } else {
          this.technicalAssumptionsSwitch = 'User Case'
        }
      }
    },
    financialInputs: {
      deep: true,
      handler () {
        if (this.loaded) {
          this.loading = true
          this.updateData(this.inputs, false).then(
            () => { this.loading = false }
          )
        }
        if (
          JSON.stringify(this.financialInputs) ===
          JSON.stringify(this.baseCaseFinancialAssumptions)
        ) {
          this.financialAssumptionsSwitch = 'Base Case'
        } else if (
          // This is to because discount rate doesn't exist on spot model
          // TODO: add a check to confirm the difference is discount rate
          Object.keys(this.differences(this.financialInputs, this.spotPrices)).length <= 1
        ) {
          this.financialAssumptionsSwitch = 'Spot Case'
        } else {
          this.financialAssumptionsSwitch = 'User Case'
        }
      }
    },
    currencySwitch (value) {
      if (value === 'CAD') {
        this.exchangeRate = this.financialInputs.USDCAD
      } else {
        this.exchangeRate = 1
      }
      this.buildTable()
    },
    accountingSwitch (value) {
      if (value === 'By-Product') {
        this.fieldSettings.AISC.apiField = 'AISC'
        this.aiscChartField = 'AISC'
      } else {
        this.fieldSettings.AISC.apiField = 'coProductAISC'
        this.aiscChartField = 'coProductAISC'
      }
      this.buildTable()
    },
    technicalAssumptionsSwitch (value) {
      if (value === 'Base Case') {
        this.technicalInputs = this.copy(this.baseCaseTechnicalAssumptions)
      }
    },
    financialAssumptionsSwitch (value) {
      if (value === 'Base Case') {
        this.financialInputs = this.copy(this.baseCaseFinancialAssumptions)
      } else if (value === 'Spot Case') {
        // TODO: Make more general.  Is there a way to update the dictionary?
        this.financialInputs.USDCAD = this.spotPrices.USDCAD
        this.financialInputs.goldPrice = this.spotPrices.goldPrice
        this.financialInputs.silverPrice = this.spotPrices.silverPrice
        this.financialInputs.copperPrice = this.spotPrices.copperPrice
        this.financialInputs.molybdenumPrice = this.spotPrices.molybdenumPrice
        this.financialInputs.rheniumPrice = this.spotPrices.rheniumPrice
      }
    },
    loaded (val) {
      if (val) {
        this.buildTable()
      }
    }
  }

}
